import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight, Coins } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreditsSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const timeoutRef = useRef(null);

  const sessionId = searchParams.get('session_id');
  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (!sessionId || !token) return;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/credits/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        if (refreshUser) {
          await refreshUser();
        }
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      timeoutRef.current = setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  }, [sessionId, token, refreshUser]);

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [sessionId, token, pollPaymentStatus]);




  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full" data-testid="credits-result-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-[#D4FF00] animate-spin mb-6" />
              <h1 className="font-heading font-bold text-2xl mb-2">Vérification du paiement...</h1>
              <p className="text-muted-foreground">Veuillez patienter quelques instants</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Crédits ajoutés !</h1>
              <p className="text-muted-foreground mb-6">
                Vos crédits ont été ajoutés à votre compte. Vous pouvez maintenant réserver des séances.
              </p>
              {paymentInfo && (
                <p className="text-lg font-bold mb-6">
                  Montant : {(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency.toUpperCase()}
                </p>
              )}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate('/trainers')}
                  className="bg-[#D4FF00] text-black hover:bg-[#bfe600] font-bold uppercase tracking-wide"
                  data-testid="book-session-btn"
                >
                  Réserver une séance
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/credits')}
                  data-testid="view-credits-btn"
                >
                  <Coins className="mr-2 w-4 h-4" />
                  Voir mes crédits
                </Button>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-yellow-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Session expirée</h1>
              <p className="text-muted-foreground mb-6">
                La session de paiement a expiré. Veuillez réessayer.
              </p>
              <Button
                onClick={() => navigate('/credits')}
                className="bg-primary text-primary-foreground font-bold"
              >
                Retourner aux crédits
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Erreur</h1>
              <p className="text-muted-foreground mb-6">
                Une erreur est survenue lors de la vérification du paiement.
                Vos crédits seront ajoutés automatiquement si le paiement a été effectué.
              </p>
              <Button
                onClick={() => navigate('/credits')}
                className="bg-primary text-primary-foreground font-bold"
              >
                Vérifier mes crédits
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
